import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { FolderSimple, FloppyDisk, Trash, FolderOpen } from "@phosphor-icons/react";

export const Projects = ({ currentResult, onLoad }) => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => api.projects().then(setItems);
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name your project first."); return; }
    if (!currentResult?.id) { toast.error("Generate something first."); return; }
    try { await api.saveProject(name, currentResult.id); setName(""); await load(); toast.success(`Saved "${name}"`); }
    catch { toast.error("Could not save project."); }
  };

  const openProject = async (id, pname) => {
    try {
      const p = await api.getProject(id);
      onLoad(p);
      setOpen(false);
      const n = (p.files || []).length;
      toast.success(`Opened "${pname}"${n ? ` — ${n} project file(s) re-attached` : ""}`);
    } catch { toast.error("Could not open project."); }
  };

  const del = async (id, pname) => { await api.deleteProject(id); await load(); toast.success(`Deleted "${pname}"`); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="projects-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <FolderSimple size={14} weight="bold" /> Projects
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-lg rounded-[4px]" data-testid="projects-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><FolderSimple size={18} weight="bold" className="text-[var(--primary)]" />Projects</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Save the current generation and reopen saved projects with their attached files.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="project-name-input"
            placeholder={currentResult ? "Name this generation to save it…" : "Generate a routine first to save it"}
            disabled={!currentResult} className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
          <Button size="sm" onClick={save} disabled={!currentResult} data-testid="project-save-btn"
            className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white shrink-0">
            <FloppyDisk size={14} /> Save
          </Button>
        </div>
        <ScrollArea className="h-[280px] border border-[var(--border)] rounded-[3px]">
          {items.length === 0 && <div className="p-3 text-xs text-[var(--text-dim)]">No saved projects yet.</div>}
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)]" data-testid={`project-${p.id}`}>
              <div className="min-w-0">
                <div className="text-[13px] truncate">{p.name}</div>
                <div className="text-[10px] font-mono text-[var(--text-dim)] truncate">{p.platform} {p.version} · {(p.files || []).length} file(s) · {p.prompt}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => openProject(p.id, p.name)} data-testid={`project-open-${p.id}`} className="h-7 rounded-[3px] text-xs gap-1"><FolderOpen size={13} />Open</Button>
                <button onClick={() => del(p.id, p.name)} data-testid={`project-del-${p.id}`} className="text-[var(--text-dim)] hover:text-[var(--alert)]"><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
